import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Lottie from 'react-lottie';
import PhoneIdentityIcon from 'assets/icons/phone-identity.svg?react';
import DesktopIcon from 'assets/icons/desktop.svg?react';
import { useClientQuery } from 'hooks/webapi';
import { Accordion, Button, Link, PortalModal, Typography } from 'components';
import PhoneUploadLottie from 'assets/lottie/phone-upload.json';
import { isTimerAlreadyStarted } from 'utils/timeoutUtility.ts';
import MagicLink from 'pages/Registration/IdentificationUploadDesktop/MagicLink/MagicLink';
import MagicLinkModal, {
  COUNTDOWN_MAGIC_LINK,
} from 'pages/Registration/MagicLinkModal/MagicLinkModal';
import styles from './ManualUploadID.module.scss';
import UploadID from 'pages/Registration/IdentificationUploadDesktop/UploadID/UploadID';
import H1 from 'components/H/H1';
import useNavigateByIdentificationStatus from 'hooks/useNavigateByIdentificationStatus';
import { useTranslation } from 'react-i18next';

const phoneUploadLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: PhoneUploadLottie,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export default function ManualUploadID() {
  const { t } = useTranslation();
  const [hasAcceptedMagicLink, setHasAcceptedMagicLink] = useState(
    isTimerAlreadyStarted(COUNTDOWN_MAGIC_LINK),
  );
  const [isModalOpen, setIsModalOpen] = useState(true);

  const { navigateByIdentificationStatus } = useNavigateByIdentificationStatus(
    'identificationUpload',
  );

  const { data: client, refetch: refetchClient } = useClientQuery();

  useEffect(() => {
    async function fetchClientAsync() {
      try {
        await refetchClient();
      } catch {
        /* empty */
      }
    }
    if (!client) {
      fetchClientAsync();
    }
  }, [client, refetchClient]);

  const startMagicLinkFlow = () => {
    setHasAcceptedMagicLink(true);
  };

  // Second doc is not required for zaplo, but leaving for future
  const isSecondDocRequired = false;

  const items = useMemo(() => {
    return [
      {
        title: t('manual_upload_id:magic_link_title'),
        icon: PhoneIdentityIcon,
        content: <MagicLink onClick={startMagicLinkFlow} />,
      },
      {
        title: t('manual_upload_id:desktop_title'),
        icon: DesktopIcon,
        content: (
          <UploadID
            buttonText={t('common:confirm')}
            onSuccess={() => navigateByIdentificationStatus()}
            secondDocRequired={isSecondDocRequired}
          />
        ),
      },
    ];
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const renderInstructionModal = useCallback(
    () => (
      <>
        <Lottie options={phoneUploadLottieOptions} />
        <div className="box-border max-w-[342px] p-6">
          <Typography className="box-border mb-9 text-center">
            {t('manual_upload_id:modal_info')}
          </Typography>
          <Button className="w-full" onClick={handleCloseModal}>
            {t('common:continue')}
          </Button>
        </div>
      </>
    ),
    [handleCloseModal],
  );

  const closeMagicLinkModal = useCallback(() => {
    setHasAcceptedMagicLink(false);
  }, []);

  return (
    <div data-test="identification-upload">
      <Helmet>
        <title>{t('manual_upload_id:html_title')}</title>
      </Helmet>

      <H1 dataTest="manualUploadId-title">{t('manual_upload_id:title')}</H1>
      <p data-test="manualUploadId-info" className="text-center lg:text-left">
        {t('manual_upload_id:info')}
      </p>
      <Accordion
        className="my-5 border-t border-b"
        items={items}
        dataTest="manualUploadId-accordion"
      />

      {/* TODO: add pending state */}
      <Link
        className="ml-7 lg:ml-0"
        dataTest="skip"
        onClick={() => navigateByIdentificationStatus()}
      >
        {t('common:skip')}
      </Link>

      {/* Modal disabled. TODO: Pending to be used to show messages according to the BE error response  */}
      {isModalOpen && false && (
        <PortalModal
          className={styles.modal}
          renderContent={renderInstructionModal}
        />
      )}

      {hasAcceptedMagicLink && (
        <MagicLinkModal
          onSuccess={navigateByIdentificationStatus}
          onCancel={closeMagicLinkModal}
        />
      )}
    </div>
  );
}
